import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StarRating } from "@/components/StarRating";
import { ReportButton } from "@/components/ReportButton";
import { MessageSquareHeart } from "lucide-react";
import { ShowMore } from "./ShowMore";

const INITIAL_ITEMS = 5;

/**
 * Phase 6: org dashboard section listing feedback the org has received,
 * with a per-item report flag that lands in the admin moderation queue.
 */
export function OrgFeedbackSection() {
  const [received, setReceived] = useState(null);
  const [limit, setLimit] = useState(INITIAL_ITEMS);

  useEffect(() => {
    api.feedbackMine().then((d) => setReceived(d.received || [])).catch(() => setReceived([]));
  }, []);

  if (received === null) return null;

  return (
    <div className="mt-10" data-testid="org-feedback-section">
      <h2 className="font-display text-2xl text-forest tracking-tight flex items-center gap-2">
        <MessageSquareHeart className="w-5 h-5 text-terracotta" /> Feedback you've received
        {received.length > 0 && <span className="text-sm font-sans text-neutral-500 font-normal">({received.length})</span>}
      </h2>
      <div className="mt-4 space-y-3">
        {received.length === 0 && (
          <div data-testid="org-feedback-empty" className="text-sm text-neutral-500">
            No feedback yet — volunteers can review your events once they've taken part.
          </div>
        )}
        {received.slice(0, limit).map((fb) => (
          <div key={fb.id} data-testid={`org-feedback-item-${fb.id}`} className="bg-white border border-neutral-200 rounded-xl p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <StarRating value={fb.rating} readonly size={16} />
              <span className="text-sm font-medium text-forest">{fb.author_name || "A volunteer"}</span>
              {fb.opportunity_title && <span className="text-xs text-neutral-500">on "{fb.opportunity_title}"</span>}
              <span className="text-xs text-neutral-400 ml-auto">{new Date(fb.created_at).toLocaleDateString()}</span>
            </div>
            {fb.text && <p className="text-sm text-neutral-700 mt-2">{fb.text}</p>}
            <div className="mt-2">
              <ReportButton targetType="feedback" targetId={fb.id} label="Report this feedback" />
            </div>
          </div>
        ))}
      </div>
      <ShowMore
        shown={Math.min(limit, received.length)}
        total={received.length}
        onMore={() => setLimit(l => l + INITIAL_ITEMS)}
        testId="org-feedback-show-more"
      />
    </div>
  );
}
